import {Column} from 'platform/components';
import {TextAlign} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNotNil} from 'ramda';

import {SalesLeagueBranchBoardResponse, SalesLeaguePeriod} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SalesLeagueBoardTable} from '../../../components/SalesLeagueBoardTable';
import {SalesLeagueTableHeaderCell} from '../../../components/SalesLeagueTableHeaderCell';
import {
  BRANCH_BOARD_ACTIONS_COLUMN_WIDTH,
  BRANCH_BOARD_RANK_COLUMN_WIDTH,
} from '../../../constants/BRANCH_BOARD_COLUMN_WIDTHS';
import {SalesLeagueBranchBoardTableRow} from './SalesLeagueBranchBoardTableRow';

/** Holds the header cell's line box open so the blank column matches the others' height. */
const BLANK_HEADER_LABEL = '\u00A0';

interface SalesLeagueBranchBoardTableProps extends RequiredTestIdProps {
  branchBoard: SalesLeagueBranchBoardResponse;
  period: SalesLeaguePeriod;
  periodLabel: string;
}

export function SalesLeagueBranchBoardTable(props: SalesLeagueBranchBoardTableProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  /*
    The columns follow the ROWS, not board-level `marginVisible`: the backend sends
    per-branch margins even when one branch withholds its own, and hiding them would
    discard data it deliberately sent. The predicate is `totalMargin`, which is null only
    when margin is hidden from this viewer — `marginPerVehicle` is also null for a branch
    that sold nothing, so it cannot tell a permission from an empty month. See
    docs/adr/0001-margin-visibility-is-gated-at-two-levels.md.
  */
  const marginColumnsVisible = props.branchBoard.rows.some((row) => isNotNil(row.totalMargin));

  const subtitle = marginColumnsVisible
    ? i18n.t('page.salesLeague.table.sortedBy')
    : i18n.t('page.salesLeague.table.sortedByWithoutMargin');

  const headerCell = (label: string, align: TextAlign, tooltip?: string) => (
    <SalesLeagueTableHeaderCell label={label} align={align} tooltip={tooltip} />
  );

  const columns: Column[] = [
    {
      element: headerCell(i18n.t('page.salesLeague.table.columnRank'), 'left'),
      width: BRANCH_BOARD_RANK_COLUMN_WIDTH,
    },
    {element: headerCell(i18n.t('entity.branch.labels.branch'), 'left')},
    {
      element: headerCell(
        i18n.t('page.salesLeague.table.columnRankChange'),
        'right',
        i18n.t('page.salesLeague.table.columnRankChangeTooltip')
      ),
    },
    {
      element: headerCell(
        i18n.t('page.salesLeague.table.columnVehiclesChange'),
        'right',
        i18n.t('page.salesLeague.table.columnVehiclesChangeTooltip')
      ),
    },
    {element: headerCell(i18n.t('page.salesLeague.table.columnVehiclesSold'), 'right')},
    {
      element: headerCell(
        i18n.t('page.salesLeague.branchBoard.table.columnVehiclesPerRep'),
        'right'
      ),
    },
    {element: headerCell(i18n.t('general.labels.gap'), 'right')},
    ...(marginColumnsVisible
      ? [
          {element: headerCell(i18n.t('page.salesLeague.table.columnMarginPerVehicle'), 'right')},
          {element: headerCell(i18n.t('page.salesLeague.table.columnTotalMargin'), 'right')},
        ]
      : []),
    // Blank header for the trailing actions cell TableRow appends. Table only renders <th>
    // for entries in `columns`, so without this the header is one cell short of every row.
    // It still renders a labelled cell, otherwise the column has no background and breaks
    // the header band.
    {
      element: headerCell(BLANK_HEADER_LABEL, 'left'),
      width: BRANCH_BOARD_ACTIONS_COLUMN_WIDTH,
    },
  ];

  return (
    <SalesLeagueBoardTable
      title={i18n.t('page.salesLeague.branchBoard.table.title')}
      subtitle={subtitle}
      columns={columns}
      rows={props.branchBoard.rows}
      getRowKey={(row) => row.branchId}
      renderRow={(row) => (
        <SalesLeagueBranchBoardTableRow
          row={row}
          currency={props.branchBoard.currency}
          marginColumnsVisible={marginColumnsVisible}
          period={props.period}
          formatCurrency={formatCurrency}
          formatNumber={formatNumber}
          data-testid={suffixTestId(`row-${row.branchId}`, props)}
        />
      )}
      footnote={i18n.t('page.salesLeague.branchBoard.table.footnote')}
      periodLabel={props.periodLabel}
      data-testid={props['data-testid']}
    />
  );
}
